import React, { useEffect, useState } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  IonText,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { apiService } from '../services/ApiService';
import { config } from '@/config/config';
import { tenantService } from '@/services/TenantService';
import seeSorLogo from '@/assets/images/SeeSOR.png';
import { useOffline } from '../contexts/OfflineContext';
import SignInForm from './login/SignInForm';
import ForgotPasswordForm from './login/ForgotPasswordForm';
import ChangePasswordForm from './login/ChangePasswordForm';
import SuccessMessageView from './login/SuccessMessageView';

type LoginView = 'login' | 'forgotPassword' | 'changePassword' | 'successMessage';

const buildDate = new Date(import.meta.env.VITE_BUILD_DATE);
const formattedBuildDate = `${buildDate.toISOString().replace('T', ' ').slice(0, 19)} UTC`;
const copyrightYear = buildDate.getUTCFullYear();

const Login: React.FC = () => {
  const [view, setView] = useState<LoginView>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [forgotUsername, setForgotUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [signInMessage, setSignInMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [changePasswordMessage, setChangePasswordMessage] = useState('Password has expired and must be changed.');
  const history = useHistory();
  const { shouldBypassLogin } = useOffline();
  const enterpriseLogoUrl = `${apiService.getBaseUrl()}/Logos/logo.png`;

  useEffect(() => {
    if (shouldBypassLogin) {
      history.push('/main');
      return;
    }

    const tempAccessToken = sessionStorage.getItem('temp_access_token');
    if (tempAccessToken) {
      setView('changePassword');
    }

    void loadSignInMessage();
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user_info');
    sessionStorage.removeItem('refresh_token');
  }, [shouldBypassLogin, history]);

  const loadSignInMessage = async () => {
    try {
      const message = await apiService.getSignInMessage();
      setSignInMessage(message || '');
    } catch (error) {
      console.error('Error loading sign-in message:', error);
      setSignInMessage('');
    }
  };

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setToastMessage('Please enter both username and password');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiService.login(username.trim(), password);

      if (result.success) {
        const isAppMode = sessionStorage.getItem('is_app') === 'true';
        const isPasswordTemp = String(result.isPasswordTemp ?? '').toLowerCase() === 'true';
        const isPasswordExpired = String(result.isPasswordExpired ?? '').toLowerCase() === 'true';

        if (isPasswordTemp || isPasswordExpired) {
          setChangePasswordMessage(
            isPasswordExpired ? 'Password has expired and must be changed.' : 'Password has been reset and must be changed.'
          );
          setView('changePassword');
          setToastMessage('Password change required.');
          setToastVariant('success');
          setShowToast(true);
          setIsLoading(false);
          return;
        }

        if (isAppMode && (window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(JSON.stringify({ signIn: true, username: username.trim() }));
        }

        setToastMessage('Login successful!');
        setToastVariant('success');
        setShowToast(true);
        setTimeout(() => {
          history.push('/main');
        }, config.transitionTime);
      } else {
        setToastMessage(result.error || 'Login failed');
        setToastVariant('error');
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage('An error occurred during login');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    const trimmedUsername = forgotUsername.trim();

    if ((!trimmedEmail && !trimmedUsername) || (trimmedEmail && trimmedUsername)) {
      setToastMessage('Please enter an email address or a user name.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    if (trimmedEmail && !config.emailAddressRegex.test(trimmedEmail)) {
      setToastMessage('Please enter a valid email address.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      const payload = trimmedEmail ? { email: trimmedEmail } : { username: trimmedUsername };
      await apiService.forgotPassword(payload);

      const message = trimmedEmail ? 'User names have been sent.' : 'Password has been reset.';
      setSuccessMessage(message);
      setView('successMessage');
      setToastMessage(message);
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      setToastMessage('An error occurred while processing your request.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim()) {
      setToastMessage('Please enter current password.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    if (!newPassword.trim()) {
      setToastMessage('Please enter a new password.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    if (!confirmPassword.trim()) {
      setToastMessage('Please confirm new password.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setToastMessage('Please confirm new password.');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      await apiService.changeAccountPassword(currentPassword, newPassword);

      const tempAccessToken = sessionStorage.getItem('temp_access_token');
      const tempUserInfo = sessionStorage.getItem('temp_user_info');
      const tempRefreshToken = sessionStorage.getItem('temp_refresh_token');

      if (tempAccessToken) {
        sessionStorage.setItem('access_token', tempAccessToken);
      }
      if (tempUserInfo) {
        sessionStorage.setItem('user_info', tempUserInfo);
      }
      if (tempRefreshToken) {
        sessionStorage.setItem('refresh_token', tempRefreshToken);
      }

      sessionStorage.removeItem('temp_access_token');
      sessionStorage.removeItem('temp_user_info');
      sessionStorage.removeItem('temp_refresh_token');

      setToastMessage('Password changed successfully');
      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => {
        history.push('/main');
      }, config.transitionTime);
    } catch (error) {
      setToastMessage('An error occurred while changing password.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    sessionStorage.setItem('is_app', 'false');
    setUsername('');
    setPassword('');
    setView('login');
  };

  const renderCurrentView = () => {
    switch (view) {
      case 'forgotPassword':
        return (
          <ForgotPasswordForm
            email={email}
            username={forgotUsername}
            isLoading={isLoading}
            onEmailChange={setEmail}
            onUsernameChange={setForgotUsername}
            onSubmit={handleForgotPassword}
            onCancel={() => {
              setEmail('');
              setForgotUsername('');
              setView('login');
            }}
          />
        );
      case 'changePassword':
        return (
          <ChangePasswordForm
            currentPassword={currentPassword}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            isLoading={isLoading}
            message={changePasswordMessage}
            onCurrentPasswordChange={setCurrentPassword}
            onNewPasswordChange={setNewPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onSubmit={handleChangePassword}
          />
        );
      case 'successMessage':
        return (
          <SuccessMessageView
            message={successMessage}
            onLogin={() => {
              setEmail('');
              setForgotUsername('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
              setView('login');
            }}
          />
        );
      case 'login':
      default:
        return (
          <SignInForm
            username={username}
            password={password}
            isLoading={isLoading}
            signInMessage={signInMessage}
            isAppMode={sessionStorage.getItem('is_app') === 'true'}
            onUsernameChange={setUsername}
            onPasswordChange={setPassword}
            onSubmit={handleLogin}
            onForgotPassword={() => {
              setEmail('');
              setForgotUsername('');
              setView('forgotPassword');
            }}
            onDisconnect={handleDisconnect}
          />
        );
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '0.75rem 0', flexWrap: 'wrap' }}>
            <img src={seeSorLogo} alt="SeeSOR logo" style={{ width: '120px', height: 'auto' }} />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent
        fullscreen
        className="ion-padding"
        style={{ '--background': 'linear-gradient(135deg, #ffffff 0%, #4782aa 100%)' } as React.CSSProperties}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '100%',
            padding: '20px',
          }}
        >
          <div style={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem', color: '#ffffff' }}>
              <img
                src={enterpriseLogoUrl}
                alt="Enterprise logo"
                onError={(event) => {
                  const target = event.currentTarget as HTMLImageElement;
                  target.style.display = 'none';
                }}
                style={{ maxWidth: '140px', maxHeight: '70px', width: 'auto', height: 'auto', objectFit: 'contain', marginBottom: '0.5rem' }}
              />
            </div>

            {renderCurrentView()}

            {view === 'login' && Capacitor.isNativePlatform() && (
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                {tenantService.getActiveTenant() && (
                  <IonText>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', margin: '0 0 0.25rem' }}>
                      Connected to: <strong>{tenantService.getActiveTenant()!.name}</strong>
                    </p>
                  </IonText>
                )}
                <button
                  onClick={() => void (async () => {
                    await tenantService.clearTenant();
                    history.push('/select-tenant');
                  })()}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  Change organization
                </button>
              </div>
            )}
          </div>

          {view === 'login' && (
            <IonText style={{ width: '100%', textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem', lineHeight: 1.45, padding: '0.5rem 0' }}>
                <div>Copyright © {copyrightYear}, ASC Group All rights reserved.</div>
                <div>SeeSOR ® is a registered trademark of ASC Group</div>
                <div>Version {import.meta.env.VITE_APP_VERSION}</div>
                <div>Page Last Updated On {formattedBuildDate}</div>
              </div>
            </IonText>
          )}
        </div>

        <Toaster
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          variant={toastVariant}
        />
      </IonContent>
    </IonPage>
  );
};

export default Login;